import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';

export type TabMenu = {
  /** Selectable items shown in the dropdown, in display order. */
  items: { key: string; label: string; badge?: number }[];
  /** Currently selected item key; '' means none selected. */
  selectedKey: string;
  /** Called with the chosen item key when an item is picked. */
  onSelectItem: (key: string) => void;
  /** Text shown when `items` is empty. */
  placeholder?: string;
};

export type TabItem = {
  key: string;
  label: string;
  // Optional count badge (e.g. outstanding items); rendered only when > 0.
  badge?: number;
  // Override badge colour classes. Defaults to the standard blue/slate palette.
  // Pass e.g. 'bg-red-500 text-white' for vendor pending-action badges.
  badgeClassName?: string;
  // When present, this tab is a "menu tab": clicking it activates the tab and
  // toggles a dropdown of `menu.items`. When absent, it renders as a normal tab.
  menu?: TabMenu;
};

// Shared button styling for both plain and menu tabs.
function tabBtnCls(isActive: boolean): string {
  return `-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2 text-sm transition ${
    isActive
      ? 'border-[#4D148C] font-semibold text-[#4D148C]'
      : 'border-transparent font-medium text-slate-600 hover:text-slate-800'
  }`;
}

function TabBadge({ tab, isActive }: { tab: TabItem; isActive: boolean }) {
  if (tab.badge === undefined || tab.badge <= 0) return null;
  return (
    <span
      className={`rounded-full px-1.5 py-0.5 text-xs font-medium ${
        tab.badgeClassName
          ? tab.badgeClassName
          : isActive
            ? 'bg-[#F3EBFA] text-[#4D148C]'
            : 'bg-slate-100 text-slate-600'
      }`}
    >
      {tab.badge}
    </span>
  );
}

type MenuPos = {
  left: number;
  minWidth: number;
  maxWidth: number;
  maxHeight: number;
  top: number | null;
  bottom: number | null;
};

// A tab that opens a single-select dropdown. The panel is rendered in a body
// portal anchored (fixed) to the trigger so an ancestor's `overflow-hidden`
// never clips it — mirrors the positioning approach in MultiSelect.tsx. Closes
// on item select, outside pointerdown, or Escape.
function MenuTab({
  tab,
  isActive,
  onActivate,
  registerRef,
}: {
  tab: TabItem;
  isActive: boolean;
  onActivate: () => void;
  registerRef: (el: HTMLButtonElement | null) => void;
}) {
  const menu = tab.menu;
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<MenuPos | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Anchor the fixed-position panel just below the trigger, left-aligned, sized
  // to its content (at least as wide as the tab) and clamped to the viewport.
  // Flips above only when there isn't room below and there's more room above.
  function reposition() {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const MARGIN = 8;
    const GAP = 4;
    const PANEL_MAX = 320;
    const left = Math.max(MARGIN, r.left);
    const maxWidth = window.innerWidth - left - MARGIN;
    const spaceBelow = window.innerHeight - r.bottom - GAP - MARGIN;
    const spaceAbove = r.top - GAP - MARGIN;
    const openUp = spaceBelow < Math.min(PANEL_MAX, 200) && spaceAbove > spaceBelow;
    const maxHeight = Math.max(96, Math.min(PANEL_MAX, openUp ? spaceAbove : spaceBelow));
    setPos(
      openUp
        ? {
            left,
            minWidth: r.width,
            maxWidth,
            maxHeight,
            top: null,
            bottom: window.innerHeight - r.top + GAP,
          }
        : { left, minWidth: r.width, maxWidth, maxHeight, top: r.bottom + GAP, bottom: null },
    );
  }

  function handleClick() {
    // A menu tab navigates to its panel only once something is selected to show
    // there. With no selection, clicking just opens the dropdown so the user can
    // pick one — they stay on whatever tab they were on until then.
    if (menu?.selectedKey) onActivate();
    if (!open) reposition();
    setOpen((o) => !o);
  }

  // While open: close on outside pointerdown or Escape, and re-anchor on
  // scroll/resize. Escape uses capture + stopPropagation so it closes only the
  // menu, not any surrounding handler.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onReposition = () => reposition();
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  if (!menu) return null;

  return (
    <>
      <button
        ref={(el) => {
          triggerRef.current = el;
          registerRef(el);
        }}
        type="button"
        role="tab"
        id={`tab-${tab.key}`}
        aria-controls={`tabpanel-${tab.key}`}
        aria-selected={isActive}
        aria-haspopup="menu"
        aria-expanded={open}
        tabIndex={isActive ? 0 : -1}
        onClick={handleClick}
        className={tabBtnCls(isActive)}
      >
        {tab.label}
        <TabBadge tab={tab} isActive={isActive} />
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            aria-label={tab.label}
            style={{
              position: 'fixed',
              left: pos.left,
              minWidth: pos.minWidth,
              maxWidth: pos.maxWidth,
              maxHeight: pos.maxHeight,
              ...(pos.top != null ? { top: pos.top } : {}),
              ...(pos.bottom != null ? { bottom: pos.bottom } : {}),
            }}
            className="z-50 overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
          >
            {menu.items.length === 0 ? (
              <p className="px-3 py-2 text-sm text-slate-400">{menu.placeholder ?? 'No options'}</p>
            ) : (
              menu.items.map((it) => {
                const selected = it.key === menu.selectedKey;
                return (
                  <button
                    key={it.key}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    onClick={() => {
                      menu.onSelectItem(it.key);
                      setOpen(false);
                      triggerRef.current?.focus();
                    }}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50 ${
                      selected ? 'font-semibold text-[#4D148C]' : 'text-slate-700'
                    }`}
                  >
                    <span className="truncate">{it.label}</span>
                    {it.badge !== undefined && it.badge > 0 && (
                      <span className="ml-auto shrink-0 rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-medium text-white">
                        {it.badge}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

// Tab strip with an underlined active tab and optional count badges. Tabs wrap
// onto additional lines when they don't fit, rather than scrolling horizontally.
// Controlled: the parent owns the active key and handles selection. Implements
// the WAI-ARIA tabs keyboard pattern (Left/Right/Home/End + roving tabindex);
// pair with a panel that has role="tabpanel" id={`tabpanel-${key}`}. A tab item
// with a `menu` renders as a dropdown (see MenuTab).
export function Tabs({
  tabs,
  active,
  onSelect,
  ariaLabel,
  className = '',
}: {
  tabs: TabItem[];
  active: string;
  onSelect: (key: string) => void;
  ariaLabel?: string;
  // Extra classes for the tab strip container (e.g. padding to align the strip
  // inside a card). Horizontal padding here keeps the full-width bottom border
  // because the container stays block-level full-width.
  className?: string;
}) {
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  function onKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    const keys = tabs.map((t) => t.key);
    const i = keys.indexOf(active);
    if (i < 0) return;
    let next = i;
    if (e.key === 'ArrowRight') next = (i + 1) % keys.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + keys.length) % keys.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = keys.length - 1;
    else return;
    e.preventDefault();
    const nextKey = keys[next]!;
    onSelect(nextKey);
    btnRefs.current[nextKey]?.focus();
  }

  return (
    <div
      className={`flex flex-wrap gap-x-1 gap-y-1 border-b border-slate-200 ${className}`}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
    >
      {tabs.map((t) => {
        const isActive = t.key === active;
        if (t.menu) {
          return (
            <MenuTab
              key={t.key}
              tab={t}
              isActive={isActive}
              onActivate={() => onSelect(t.key)}
              registerRef={(el) => {
                btnRefs.current[t.key] = el;
              }}
            />
          );
        }
        return (
          <button
            key={t.key}
            ref={(el) => {
              btnRefs.current[t.key] = el;
            }}
            type="button"
            role="tab"
            id={`tab-${t.key}`}
            aria-controls={`tabpanel-${t.key}`}
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onSelect(t.key)}
            className={tabBtnCls(isActive)}
          >
            {t.label}
            <TabBadge tab={t} isActive={isActive} />
          </button>
        );
      })}
    </div>
  );
}
